/** Workflow role codes returned by GET /OnBoarding/userroles and login/settings. */
export const WorkflowRoleCode = {
  CUSTOMER: "customer",
  IMPLEMENTATION_TEAM: "implementationTeam",
  TPM: "tpm",
  ADMIN: "admin",
} as const;

export type WorkflowRoleCodeValue =
  (typeof WorkflowRoleCode)[keyof typeof WorkflowRoleCode];

/** Onboarding workflow status values from GET …/GetSettings. */
export const WorkflowStatus = {
  DRAFT: "draft",
  CUSTOMER_SUBMITTED: "customerSubmitted",
  IMPLEMENTATION_SUBMITTED: "implementationSubmitted",
  SUBMITTED: "submitted",
} as const;

export function normalizeRoleCode(code: string | undefined | null): string {
  return String(code ?? "")
    .trim()
    .toLowerCase();
}

export function normalizeWorkflowStatus(
  status: string | undefined | null,
): string {
  return String(status ?? WorkflowStatus.DRAFT)
    .trim()
    .toLowerCase();
}

/** Role that may submit at the current workflow stage; null when workflow is complete. */
export function getSubmitRoleForStatus(
  status: string | undefined | null,
): string | null {
  const s = normalizeWorkflowStatus(status);
  switch (s) {
    case WorkflowStatus.DRAFT.toLowerCase():
      return WorkflowRoleCode.CUSTOMER;
    case WorkflowStatus.CUSTOMER_SUBMITTED.toLowerCase():
      return WorkflowRoleCode.IMPLEMENTATION_TEAM;
    case WorkflowStatus.IMPLEMENTATION_SUBMITTED.toLowerCase():
      return WorkflowRoleCode.TPM;
    case WorkflowStatus.SUBMITTED.toLowerCase():
      return null;
    default:
      if (s.includes("implement")) return WorkflowRoleCode.IMPLEMENTATION_TEAM;
      if (s.includes("tpm")) return WorkflowRoleCode.TPM;
      if (s.includes("submit")) return null;
      return WorkflowRoleCode.CUSTOMER;
  }
}

export function inferWorkflowReadOnly(
  userRoleCode: string | undefined | null,
  status: string | undefined | null,
  apiReadOnly?: boolean,
): boolean {
  if (typeof apiReadOnly === "boolean") return apiReadOnly;
  const normalizedStatus = normalizeWorkflowStatus(status);
  if (normalizedStatus === WorkflowStatus.SUBMITTED.toLowerCase()) {
    return true;
  }
  const requiredSubmitRole = getSubmitRoleForStatus(status);
  if (!requiredSubmitRole) return true;
  return (
    normalizeRoleCode(userRoleCode) !== normalizeRoleCode(requiredSubmitRole)
  );
}

export function canRoleSubmit(
  userRoleCode: string | undefined | null,
  status: string | undefined | null,
): boolean {
  const required = getSubmitRoleForStatus(status);
  if (!required) return false;
  return normalizeRoleCode(userRoleCode) === normalizeRoleCode(required);
}

export function isCustomerRole(code: string | undefined | null): boolean {
  return normalizeRoleCode(code) === WorkflowRoleCode.CUSTOMER;
}

export function isInternalWorkflowRole(
  code: string | undefined | null,
): boolean {
  const normalized = normalizeRoleCode(code);
  return (
    normalized !== WorkflowRoleCode.CUSTOMER &&
    normalized !== "" &&
    normalized !== "unknown"
  );
}

export function getWorkflowStatusLabel(
  status: string | undefined | null,
): string {
  const s = normalizeWorkflowStatus(status);
  switch (s) {
    case WorkflowStatus.DRAFT.toLowerCase():
      return "Draft — awaiting customer submission";
    case WorkflowStatus.CUSTOMER_SUBMITTED.toLowerCase():
      return "Customer submitted — implementation team review";
    case WorkflowStatus.IMPLEMENTATION_SUBMITTED.toLowerCase():
      return "Implementation submitted — TPM final review";
    case WorkflowStatus.SUBMITTED.toLowerCase():
      return "Submitted — onboarding complete";
    default:
      return `Status: ${status ?? "unknown"}`;
  }
}

/** Maps legacy settings `role` strings to workflow role codes. */
export function legacyRoleToCode(role: unknown): string {
  if (typeof role === "string") {
    const r = role.trim().toLowerCase();
    if (r === "customer") return WorkflowRoleCode.CUSTOMER;
    if (r === "agilysys" || r === "implementationteam") {
      return WorkflowRoleCode.IMPLEMENTATION_TEAM;
    }
    if (r === "tpm") return WorkflowRoleCode.TPM;
    if (r === "admin") return WorkflowRoleCode.ADMIN;
    return r;
  }
  if (typeof role === "number") {
    if (role === 2) return WorkflowRoleCode.ADMIN;
    if (role === 1) return WorkflowRoleCode.IMPLEMENTATION_TEAM;
    return WorkflowRoleCode.CUSTOMER;
  }
  return WorkflowRoleCode.CUSTOMER;
}
